import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FloatingCTA from '../../../components/feature/FloatingCTA';
import { faqs } from '../../../mocks/faqData';

export default function DryerVentCleaningPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const ventFaqs = faqs.filter((f) => f.category === 'dryervent');

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/cf733292ad22aa26738441cdd15bee47.jpg"
            alt="Dryer vent cleaning Jacksonville FL - BBM Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/90 via-brand-dark/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-5">
              <div className="h-px w-8 bg-brand-amber" />
              Dryer Vent Cleaning Jacksonville FL
            </div>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
              Dryer Vent Cleaning That Prevents Fires &amp; Restores Performance
            </h1>
            <p className="text-white/80 text-xl leading-relaxed mb-7">
              The USFA reports nearly 2,900 dryer fires annually. BBM Services provides fast, affordable dryer vent cleaning in Jacksonville, FL — reducing your fire risk and cutting drying time in half.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-4 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap transition-all text-center">Book Dryer Vent Cleaning</Link>
              <a href="tel:+19045550191" className="flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white/60 text-white font-semibold py-4 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap transition-all">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill" /></div>
                (904) 555-0191
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-14 items-start">
            <div className="lg:w-3/5">
              <h2 className="font-serif text-4xl font-bold text-brand-dark mb-5">Why Dryer Vent Cleaning Matters</h2>
              <p className="text-brand-warm-gray text-lg leading-relaxed mb-4">
                Dryer vent cleaning removes the lint, debris, and blockages that accumulate inside your dryer exhaust duct over time. Lint is highly flammable — and when it builds up inside a dryer vent, it restricts airflow, causes the dryer to overheat, and creates a serious fire hazard in your home.
              </p>
              <p className="text-brand-warm-gray text-base leading-relaxed mb-4">
                The U.S. Fire Administration reports that failure to clean the dryer vent is the leading cause of residential dryer fires. Beyond fire risk, a clogged vent significantly reduces your dryer's efficiency — meaning longer drying cycles, higher energy bills, and a shorter appliance lifespan.
              </p>
              <p className="text-brand-warm-gray text-base leading-relaxed mb-8">
                BBM Services performs a complete dryer vent cleaning in 45–90 minutes, removing all lint and blockages, inspecting the full vent line, and testing performance after the service. It's one of the most affordable and impactful home safety services available to Jacksonville homeowners.
              </p>

              <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-500 rounded-lg flex-shrink-0 mt-0.5">
                    <i className="ri-fire-fill text-white text-sm" />
                  </div>
                  <div>
                    <strong className="text-brand-dark text-sm">Fire Safety Alert</strong>
                    <p className="text-brand-warm-gray text-sm mt-1">The USFA recommends cleaning your dryer exhaust duct at least once per year. Longer vent runs, crushed or kinked ducts, and bird nests can require more frequent cleaning.</p>
                  </div>
                </div>
              </div>

              <h3 className="font-serif text-2xl font-bold text-brand-dark mb-4">Signs Your Dryer Vent Needs Cleaning</h3>
              <ul className="space-y-2 mb-8">
                {['Clothes taking longer than one cycle to dry', 'Dryer or clothes feel extremely hot after a cycle', 'Burning smell during dryer operation', 'Dryer shuts off mid-cycle', 'Visible lint buildup around the dryer connection', 'Exterior vent cap not opening when dryer runs', 'More than 12 months since last cleaning'].map((s) => (
                  <li key={s} className="flex items-center gap-2 text-sm text-brand-light">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-check-line text-brand-amber" /></div>
                    {s}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:w-2/5 space-y-5">
              <div className="bg-brand-dark rounded-2xl p-7 text-white">
                <h3 className="font-serif text-2xl font-bold mb-5">Dryer Vent Cleaning Pricing</h3>
                <div className="space-y-4">
                  {[
                    { service: 'Standard Dryer Vent Cleaning', price: 'From $99' },
                    { service: 'Long or Complex Vent Run', price: 'From $129' },
                    { service: 'Vent Reroute / Repair', price: 'Custom Quote' },
                    { service: 'Bundle: Duct + Dryer Vent', price: 'Save 15%' },
                  ].map((item) => (
                    <div key={item.service} className="flex items-center justify-between border-b border-white/10 pb-4 last:border-0 last:pb-0">
                      <span className="text-white/80 text-sm">{item.service}</span>
                      <span className="text-brand-amber font-bold text-sm whitespace-nowrap">{item.price}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact" className="block bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-3.5 text-center rounded-lg mt-6 cursor-pointer whitespace-nowrap transition-all text-sm">Get a Free Quote</Link>
              </div>
              <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
                <h4 className="font-semibold text-brand-dark mb-4">What's Included</h4>
                {['Full vent line inspection', 'Complete lint removal', 'Blockage and obstruction clearing', 'Exterior vent cap inspection & cleaning', 'Performance test after service', 'Fire safety check included', 'Certificate of cleaning available'].map((f) => (
                  <div key={f} className="flex items-center gap-2 py-2 border-b border-stone-100 last:border-0">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-check-double-line text-brand-amber text-sm" /></div>
                    <span className="text-brand-light text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-brand-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl font-bold text-brand-dark mb-8 text-center">Dryer Vent Cleaning FAQs</h2>
          <div className="space-y-3">
            {ventFaqs.map((faq) => (
              <div key={faq.id} className={`border rounded-xl overflow-hidden transition-all ${openId === faq.id ? 'border-brand-amber/40 bg-amber-50/50' : 'border-stone-200 bg-white'}`}>
                <button className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left cursor-pointer" onClick={() => setOpenId(openId === faq.id ? null : faq.id)}>
                  <span className={`font-semibold text-sm leading-snug ${openId === faq.id ? 'text-brand-amber' : 'text-brand-dark'}`}>{faq.question}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${openId === faq.id ? 'bg-brand-amber text-white' : 'bg-stone-100 text-brand-warm-gray'}`}>
                    <i className={`${openId === faq.id ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                  </div>
                </button>
                {openId === faq.id && <div className="px-6 pb-4"><p className="text-brand-warm-gray text-sm leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 bg-brand-amber">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-3">Book Your Dryer Vent Cleaning Today</h2>
          <p className="text-white/80 text-lg mb-6">Starting at $99 · 45–90 minutes · Jacksonville, FL</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-brand-amber font-bold py-3.5 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap hover:bg-amber-50 transition-all">Book Now</Link>
            <a href="tel:+19045550191" className="border-2 border-white/40 text-white font-semibold py-3.5 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap hover:border-white transition-all">Call (904) 555-0191</a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
